import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Col, Form, OverlayTrigger, Row, Tooltip} from 'react-bootstrap';
import {pdf} from '@react-pdf/renderer';
import FilledPristupnicaDocument from './FilledPristupnicaDocument'; // Path to your PDF document component
import {saveAs} from 'file-saver';

const PristupnicaForm = ({onSubmit, initialData = {}}) => {
    const [formData, setFormData] = useState({
        // Section 1: Podatci o poslovnom subjektu
        naziv: initialData.naziv || '',
        maticniBroj: initialData.maticniBroj || '',
        oib: initialData.oib || '',
        adresa: initialData.adresa || '',
        drzava: initialData.drzava || '',
        brojPoste: initialData.brojPoste || '',
        isoKodDrzave: initialData.isoKodDrzave || '',
        // Section 2: Poslovna jedinica
        sifraPoslovneJedinice: initialData.sifraPoslovneJedinice || '',
        // Sectuon 3: PEPPOL
        peppolKorisnik: initialData.peppolKorisnik || false,
        // Section 4: Podatci o korisniku servisa
        nacinPristupa: initialData.nacinPristupa || '', // 'finaKripto' or 'appCert'
        korisnikImePrezime: initialData.korisnikImePrezime || '',
        korisnikAdresa: initialData.korisnikAdresa || '',
        korisnikOIB: initialData.korisnikOIB || '',
        korisnikBrojPoste: initialData.korisnikBrojPoste || '',
        korisnikTelefon: initialData.korisnikTelefon || '',
        korisnikEmail: initialData.korisnikEmail || '',
        nazivAplikacijskogCertifikata: initialData.nazivAplikacijskogCertifikata || '',
        nazivPosluzitelja: initialData.nazivPosluzitelja || '',
        emailPoslovnogSubjekta: initialData.emailPoslovnogSubjekta || '',
        // Section 5: Cjenovni paket
        cjenovniPaket: initialData.cjenovniPaket || '', // e.g., 'XS15'
        // Section 6: Prava korisnika - Fina e-Račun
        eRacunUnos: initialData.eRacunUnos || false,
        eRacunPotpisSlanje: initialData.eRacunPotpisSlanje || false,
        eRacunPregledDohvat: initialData.eRacunPregledDohvat || false,
        eRacunSlanjeObavijestiPlacanje: initialData.eRacunSlanjeObavijestiPlacanje || false,
        eRacunPristupSvimPJ: initialData.eRacunPristupSvimPJ || false,
        eRacunPravaDrugoIme: initialData.eRacunPravaDrugoIme || false,
        // Section 7: Prava korisnika - Fina e-Arhiv
        eArhivAdministrator: initialData.eArhivAdministrator || false,
        eArhivKorisnik: initialData.eArhivKorisnik || false,
        eArhivPristupSvimPJ: initialData.eArhivPristupSvimPJ || false,
        eArhivPravaDrugoIme: initialData.eArhivPravaDrugoIme || false,
        // Section 8: Prava korisnika - Fina Faktoring Platforma
        faktoringProdavateljPregled: initialData.faktoringProdavateljPregled || false,
        faktoringProdavateljPotpis: initialData.faktoringProdavateljPotpis || false,
        faktoringDuznik: initialData.faktoringDuznik || false,
        faktoringPravaDrugoIme: initialData.faktoringPravaDrugoIme || false,
        // Potpisi
        mjesto: initialData.mjesto || '',
        datum: initialData.datum || new Date().toISOString().slice(0, 10), //YYYY-MM-DD
        ovlastenikImePrezime: initialData.ovlastenikImePrezime || '',
    });

    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});
    const [sifraPJVrijednost, setSifraPJVrijednost] = useState(formData.sifraPoslovneJedinice !== '');

    useEffect(() => {
        setSifraPJVrijednost(formData.sifraPoslovneJedinice !== '');
    }, [formData.sifraPoslovneJedinice]);

    const disableFaktoringPotpisAndDuznik = formData.nacinPristupa === 'appCert';

    useEffect(() => {
        if (disableFaktoringPotpisAndDuznik) {
            setFormData(prev => ({
                ...prev,
                faktoringProdavateljPotpis: false,
                faktoringDuznik: false,
            }));
        }
    }, [disableFaktoringPotpisAndDuznik]);

    useEffect(() => {

        if (
            formData.nacinPristupa === 'appCert' &&
            !formData.eRacunPregledDohvat &&
            !formData.eRacunPotpisSlanje
        ) {
            return;
        }

        const isAnyPravoSelected = [
            'eRacunUnos', 'eRacunPotpisSlanje', 'eRacunPregledDohvat',
            'eRacunSlanjeObavijestiPlacanje', 'eRacunPristupSvimPJ', 'eRacunPravaDrugoIme',
            'eArhivAdministrator', 'eArhivKorisnik', 'eArhivPristupSvimPJ', 'eArhivPravaDrugoIme',
            'faktoringProdavateljPregled', 'faktoringProdavateljPotpis', 'faktoringDuznik',
            'faktoringPravaDrugoIme'
        ].some(key => formData[key]);

        if (isAnyPravoSelected && validationErrors.pravaKorisnika) {
            setValidationErrors(prevErrors => {
                const newErrors = {...prevErrors};
                delete newErrors.pravaKorisnika;
                return newErrors;
            });
        }
    }, [formData, validationErrors.pravaKorisnika]);

    // Validation logic for individual fields
    const validateField = useCallback((name, value, type) => {
        let errorMessage = '';
        let isValid = true;

        if (type !== 'checkbox' && !value && document.querySelector(`[name="${name}"]`)?.required) {
            errorMessage = 'Ovo polje je obavezno.';
            isValid = false;
        } else {
            switch (name) {
                case 'maticniBroj':
                    if (value && !/^\d{8}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan matični broj (8 znamenki).';
                        isValid = false;
                    }
                    break;
                case 'brojPoste':
                    if (value && !/^\d{5}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan broj pošte.';
                        isValid = false;
                    }
                    break;
                case 'oib':
                    if (value && !/^\d{11}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan OIB (11 znamenki).';
                        isValid = false;
                    }
                    break;
                case 'isoKodDrzave':
                    if (value && !/^[a-zA-Z]{2}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan ISO kod države (dvije slovne oznake).';
                        isValid = false;
                    }
                    break;
                case 'sifraPoslovneJedinice':
                    if (value && !/^\w{1,14}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravnu šifru poslovne jedinice.';
                        isValid = false;
                    }
                    break;
                case 'emailPoslovnogSubjekta':
                    if (formData.nacinPristupa === 'appCert') {
                        const emailInput = document.querySelector(
                            '[name="emailPoslovnogSubjekta"]'
                        );

                        if (!value) {
                            errorMessage = 'E-mail poslovnog subjekta je obavezan.';
                            isValid = false;
                        } else if (emailInput && !emailInput.checkValidity()) {
                            errorMessage = 'Molimo unesite ispravnu e-mail adresu.';
                            isValid = false;
                        }
                    }
                    break;
                case 'korisnikOIB':
                    if (formData.nacinPristupa === 'finaKripto' && value && !/^\d{11}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan OIB (11 znamenki).';
                        isValid = false;
                    }
                    break;
                case 'korisnikBrojPoste':
                    if (formData.nacinPristupa === 'finaKripto' && value && !/^\d{5}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan broj pošte.';
                        isValid = false;
                    }
                    break;
                case 'korisnikTelefon':
                    if (formData.nacinPristupa === 'finaKripto' && value && !/^\+?[0-9\s-/]{3,25}$/.test(value)) {
                        errorMessage = 'Molimo unesite ispravan broj telefona.';
                        isValid = false;
                    }
                    break;
                case 'korisnikEmail':
                    if (formData.nacinPristupa === 'finaKripto') {
                        const emailInput = document.querySelector(
                            '[name="korisnikEmail"]'
                        );

                        if (!value) {
                            errorMessage = 'E-mail adresa je obavezna.';
                            isValid = false;
                        } else if (emailInput && !emailInput.checkValidity()) {
                            errorMessage = 'Molimo unesite ispravnu e-mail adresu.';
                            isValid = false;
                        }
                    }
                    break;
                case 'nacinPristupa':
                    if (!value) {
                        errorMessage = 'Molimo odaberite način pristupa.';
                        isValid = false;
                    }
                    break;
                case 'cjenovniPaket':
                    if (!value) {
                        errorMessage = 'Molimo odaberite cjenovni paket.';
                        isValid = false;
                    }
                    break;
                // Add more specific validations here as needed
                default:
                    break;
            }
        }

        setValidationErrors(prevErrors => ({
            ...prevErrors,
            [name]: errorMessage,
        }));
        return isValid;
    }, [formData.nacinPristupa]); // Depend on nacinPristupa for conditional validation messages

    const handleChange = (event) => {
        const {name, value, type, checked} = event.target;

        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));

        // If the field has been touched, re-validate it immediately
        if (touchedFields[name]) {
            validateField(name, type === 'checkbox' ? checked : value, type);
        } else {
            // Clear error message if user starts typing after an error was shown on blur
            setValidationErrors(prevErrors => {
                if (prevErrors[name]) {
                    return {...prevErrors, [name]: ''};
                }
                return prevErrors;
            });
        }
    };

    const handleBlur = (event) => {
        const {name, value, type} = event.target;
        setTouchedFields(prev => ({
            ...prev,
            [name]: true
        }));
        validateField(name, value, type);
    };

    const validateFormData = () => {

        // Mark all fields as touched for full validation display on submit
        const allTouched = Object.keys(formData).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});
        setTouchedFields(allTouched);

        let formIsValid = true;
        const newErrors = {};

        // Run validation for all fields on submit
        Object.keys(formData).forEach(name => {
            const inputElement = document.querySelector(`[name="${name}"]`);
            const value = formData[name];
            const type = inputElement ? inputElement.type : typeof value === 'boolean' ? 'checkbox' : 'text'; // Infer type for non-input fields

            let errorMessage = '';

            if (type !== 'checkbox' && inputElement?.required && !value) {
                errorMessage = 'Ovo polje je obavezno.';
            } else {
                switch (name) {
                    case 'maticniBroj':
                        if (value && !/^\d{8}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan matični broj (8 znamenki).';
                        }
                        break;
                    case 'brojPoste':
                        if (value && !/^\d{5}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan broj pošte.';
                        }
                        break;
                    case 'oib':
                        if (value && !/^\d{11}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan OIB (11 znamenki).';
                        }
                        break;
                    case 'isoKodDrzave':
                        if (value && !/^[a-zA-Z]{2}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan ISO kod države (dvije slovne oznake).';
                        }
                        break;
                    case 'sifraPoslovneJedinice':
                        if (value && !/^\w{1,14}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravnu šifru poslovne jedinice.';
                        }
                        break;
                    case 'emailPoslovnogSubjekta':
                        if (formData.nacinPristupa === 'appCert') {
                            const emailInput = document.querySelector(
                                '[name="emailPoslovnogSubjekta"]'
                            );

                            if (!value) {
                                errorMessage = 'E-mail poslovnog subjekta je obavezan.';
                            } else if (emailInput && !emailInput.checkValidity()) {
                                errorMessage = 'Molimo unesite ispravnu e-mail adresu.';
                            }
                        }
                        break;
                    case 'korisnikOIB':
                        if (formData.nacinPristupa === 'finaKripto' && value && !/^\d{11}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan OIB (11 znamenki).';
                        }
                        break;
                    case 'korisnikBrojPoste':
                        if (formData.nacinPristupa === 'finaKripto' && value && !/^\d{5}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan broj pošte.';
                        }
                        break;
                    case 'korisnikTelefon':
                        if (formData.nacinPristupa === 'finaKripto' && value && !/^\+?[0-9\s-/]{3,25}$/.test(value)) {
                            errorMessage = 'Molimo unesite ispravan broj telefona.';
                        }
                        break;
                    case 'korisnikEmail':
                        if (formData.nacinPristupa === 'finaKripto') {
                            const emailInput = document.querySelector(
                                '[name="korisnikEmail"]'
                            );

                            if (!value) {
                                errorMessage = 'E-mail adresa je obavezna.';
                            } else if (emailInput && !emailInput.checkValidity()) {
                                errorMessage = 'Molimo unesite ispravnu e-mail adresu.';
                            }
                        }
                        break;
                    case 'nacinPristupa':
                        if (!value) {
                            errorMessage = 'Molimo odaberite način pristupa.';
                        }
                        break;
                    case 'cjenovniPaket':
                        if (!value) {
                            errorMessage = 'Molimo odaberite cjenovni paket.';
                        }
                        break;
                    // Add more specific validations here as needed
                    default:
                        break;
                }
            }
            if (errorMessage) {
                newErrors[name] = errorMessage;
                formIsValid = false;
            }
        });

        if (formData.nacinPristupa === 'appCert') {
            if (!formData.eRacunPregledDohvat && !formData.eRacunPotpisSlanje) {
                newErrors.pravaKorisnika =
                    'Za aplikacijski certifikat morate odabrati "Pregled i dohvat" i/ili "Potpis i slanje".';
                formIsValid = false;
            }
        }

        const isAnyPravoSelected = [
            'eRacunUnos', 'eRacunPotpisSlanje', 'eRacunPregledDohvat',
            'eRacunSlanjeObavijestiPlacanje', 'eRacunPristupSvimPJ', 'eRacunPravaDrugoIme',
            'eArhivAdministrator', 'eArhivKorisnik', 'eArhivPristupSvimPJ', 'eArhivPravaDrugoIme',
            'faktoringProdavateljPregled', 'faktoringProdavateljPotpis', 'faktoringDuznik',
            'faktoringPravaDrugoIme'
        ].some(key => formData[key]);

        if (!isAnyPravoSelected) {
            newErrors.pravaKorisnika = 'Molimo odaberite barem jedno pravo korisnika.';
            formIsValid = false;
        }

        setValidationErrors(newErrors);

        // Smooth scroll to the first error
        setTimeout(() => {
            const firstInvalidField = document.querySelector('.is-invalid');
            if (firstInvalidField) {
                firstInvalidField.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }, 0);

        return formIsValid;
    };

    return (
        <Form noValidate>
            <Card className="mb-3">
                <Card.Header as="h5">1. Podatci o poslovnom subjektu</Card.Header>
                <Card.Body>
                    <Card.Text>
                        U slučaju kada su dvije fizičke osobe (svaka sa svojim OIB-om) suvlasnici jedne registrirane
                        djelatnosti (jedan MB), ovom Pristupnicom potrebno je odrediti čiji će OIB biti korišten za
                        ugovaranje paketa. Ako jedna fizična osoba (jedan OIB) ima više registriranih djelatnosti (svaka
                        s različitim MB-om), za svaku djelatnost popunjava se zasebna Pristupnica.
                    </Card.Text>
                    <Row className="mb-3">
                        <Form.Group as={Col} md="12" controlId="formNaziv">
                            <Form.Label>Naziv</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="naziv"
                                value={formData.naziv}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.naziv && !validationErrors.naziv}
                                isInvalid={touchedFields.naziv && !!validationErrors.naziv}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.naziv || 'Molimo unesite naziv.'}</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                    <Row className="mb-3">
                        <Form.Group as={Col} md="6" controlId="formMaticniBroj">
                            <Form.Label>Matični broj</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="maticniBroj"
                                value={formData.maticniBroj}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                pattern="\d{8}"
                                isValid={touchedFields.maticniBroj && !validationErrors.maticniBroj}
                                isInvalid={touchedFields.maticniBroj && !!validationErrors.maticniBroj}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.maticniBroj || 'Molimo unesite ispravan matični broj (8 znamenki).'}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="formOIB">
                            <Form.Label>OIB</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="oib"
                                value={formData.oib}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                pattern="\d{11}"
                                isValid={touchedFields.oib && !validationErrors.oib}
                                isInvalid={touchedFields.oib && !!validationErrors.oib}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.oib || 'Molimo unesite ispravan OIB (11 znamenki).'}</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                    <Row className="mb-3">
                        <Form.Group as={Col} md="8" controlId="formAdresa">
                            <Form.Label>Adresa (mjesto, ulica i broj)</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="adresa"
                                value={formData.adresa}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.adresa && !validationErrors.adresa}
                                isInvalid={touchedFields.adresa && !!validationErrors.adresa}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.adresa || 'Molimo unesite adresu.'}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="4" controlId="formBrojPoste">
                            <Form.Label>Broj pošte</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="brojPoste"
                                value={formData.brojPoste}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.brojPoste && !validationErrors.brojPoste}
                                isInvalid={touchedFields.brojPoste && !!validationErrors.brojPoste}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.brojPoste || 'Molimo unesite broj pošte.'}</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                    <Row className="mb-3">
                        <Form.Group as={Col} md="8" controlId="formDrzava">
                            <Form.Label>Država</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="drzava"
                                value={formData.drzava}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.drzava && !validationErrors.drzava}
                                isInvalid={touchedFields.drzava && !!validationErrors.drzava}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.drzava || 'Molimo unesite državu.'}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="4" controlId="formIsoKod">
                            <Form.Label>ISO kod države*</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="isoKodDrzave"
                                value={formData.isoKodDrzave}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                pattern="\w{2}"
                                isValid={touchedFields.isoKodDrzave && !validationErrors.isoKodDrzave}
                                isInvalid={touchedFields.isoKodDrzave && !!validationErrors.isoKodDrzave}
                            />
                            <Card.Text>
                                *ISO 3166, dvoslovni kod; međunarodna kratica za državu (npr. HR).
                            </Card.Text>
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.isoKodDrzave || 'Molimo unesite ispravan ISO kod države (dvije slovne oznake).'}</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h5">2. Poslovna jedinica</Card.Header>
                <Card.Body>
                    <Form.Group as={Col} md="12" controlId="formSifraPJ">
                        <Card.Text>
                            Ako imate više poslovnih jedinica, <span className="font-weight-bold">prvo ih morate registrirati</span> ispunjavanjem <a
                            href="https://www.fina.hr/ngsite/content/download/16116/252900/1" target="_blank"
                            rel="noopener noreferrer">Pristupnice za registraciju poslovnog subjekta s poslovnim
                            jedinicama na servise paketa Fina e-Račun PLUS</a>.
                            <br/>
                            <br/>
                            Na ovom mjestu možete dodijeliti prava korisniku <span className="font-weight-bold">za jednu ili više poslovnih jedinica</span>.
                            Ako korisniku želite dodijeliti <span
                            className="font-weight-bold">različita prava</span> (vidi dio „Prava korisnika“) za više
                            poslovnih jedinica, za svaku je potrebno ispuniti ovu pristupnicu s pripadajućom šifrom. U
                            slučaju da želite dodijeliti <span className="font-weight-bold">ista prava</span> za sve
                            poslovne jedinice, tada se šifra ne upisuje, a dovoljno je ispuniti samo ovu pristupnicu uz
                            prethodnu registraciju poslovnih jedinica.
                            <br/>
                            <br/>
                            Šifra poslovne jedinice mora odgovarati onoj navedenoj u pristupnici za registraciju
                            poslovnih jedinica.
                        </Card.Text>
                        <Form.Label>Šifra poslovne jedinice</Form.Label>
                        <Form.Control type="text" name="sifraPoslovneJedinice" value={formData.sifraPoslovneJedinice}
                                      onChange={handleChange} onBlur={handleBlur}
                                      isValid={touchedFields.isoKodDrzave && !validationErrors.isoKodDrzave}
                                      isInvalid={touchedFields.sifraPoslovneJedinice && !!validationErrors.sifraPoslovneJedinice}/>
                        <Form.Control.Feedback
                            type="invalid">{validationErrors.sifraPoslovneJedinice || 'Molimo unesite ispravnu šifru poslovne jedinice.'}</Form.Control.Feedback>
                    </Form.Group>
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h5">3. Prekogranična razmjena računa</Card.Header>
                <Card.Body>
                    <Form.Check>
                        <Form.Check.Input
                            type="checkbox"
                            name="peppolKorisnik"
                            checked={formData.peppolKorisnik}
                            onChange={handleChange}
                        />
                        <Form.Check.Label>PEPPOL korisnik</Form.Check.Label>
                        <Card.Text>
                            Označava funkcionalnost slanja i zaprimanja prekograničnih e-računa putem PEPPOL mreže.
                            <br/>
                            Ova funkcionalnost se dodatno naplaćuje prema redovnom Cjeniku Fine i Uvjetima korištenja
                            usluge.
                        </Card.Text>

                    </Form.Check>
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h5">4. Podatci o korisniku servisa</Card.Header>
                <Card.Body>
                    <Card.Text>
                        Osoba ovlaštena za zastupanje <span
                        className="font-weight-bold">određuje korisnika servisa</span> Fina e-Račun, Fina e-Arhiv i Fina
                        Faktoring Platforma.
                        <br/>
                        <span className="font-weight-bold">Označite jedan</span> od dva ponuđena načina pristupa
                        navedenim servisima i ispunite pripadajuće podatke u nastavku.
                        <br/>
                        <br/>
                        <span className="font-weight-bold">Odabir jedne opcije je obavezan!</span>
                    </Card.Text>
                    <Form.Group className="mb-3">
                        <Form.Check type="radio" label="Pristup putem Fina kriptouređaja (USB stick/e-kartica)"
                                    name="nacinPristupa" value="finaKripto"
                                    checked={formData.nacinPristupa === 'finaKripto'}
                                    onChange={handleChange} onBlur={handleBlur} required id="radioFinaKripto"
                                    isInvalid={touchedFields.nacinPristupa && !!validationErrors.nacinPristupa}
                        />
                        <Form.Text muted>Ovaj odabir vam omogućuje pristup servisima Fina e-Račun, Fina e-Arhiv i Fina
                            Faktoring platforma putem web aplikacije.</Form.Text>
                        <Form.Check type="radio" label="Pristup aplikacijskim i/ili poslužiteljskim certifikatom"
                                    name="nacinPristupa" value="appCert" checked={formData.nacinPristupa === 'appCert'}
                                    onChange={handleChange} onBlur={handleBlur} required id="radioAppCert"
                                    isInvalid={touchedFields.nacinPristupa && !!validationErrors.nacinPristupa}
                        />
                        <Form.Text muted>Ovaj odabir vam omogućava izravno povezivanje sa servisima Fina e-Račun i Fina
                            e-Arhiv putem računovodstvenog programa koji
                            koristite. Svoj aplikacijski certifikat možete koristiti i za pristup web aplikaciji Fina
                            e-Arhiv i Fina Faktoring platforma.</Form.Text>
                        <Form.Control.Feedback type="invalid"
                                               style={{display: touchedFields.nacinPristupa && !!validationErrors.nacinPristupa ? 'block' : 'none'}}>
                            {validationErrors.nacinPristupa || 'Molimo odaberite način pristupa.'}
                        </Form.Control.Feedback>
                    </Form.Group>
                    {formData.nacinPristupa === 'finaKripto' && (
                        <>
                            <Row className="mb-3">
                                <Form.Group as={Col} md="6" controlId="formKorisnikIme">
                                    <Form.Label>Ime i prezime</Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        name="korisnikImePrezime"
                                        value={formData.korisnikImePrezime}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touchedFields.korisnikImePrezime && !validationErrors.korisnikImePrezime}
                                        isInvalid={touchedFields.korisnikImePrezime && !!validationErrors.korisnikImePrezime}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikImePrezime || 'Molimo unesite ime i prezime.'}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} md="6" controlId="formKorisnikOIB">
                                    <Form.Label>OIB korisnika</Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        name="korisnikOIB"
                                        value={formData.korisnikOIB}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        pattern="\d{11}"
                                        isValid={touchedFields.korisnikOIB && !validationErrors.korisnikOIB}
                                        isInvalid={touchedFields.korisnikOIB && !!validationErrors.korisnikOIB}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikOIB || 'Molimo unesite ispravan OIB (11 znamenki).'}</Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} md="8" className="mb-3" controlId="formKorisnikAdresa">
                                    <Form.Label>Adresa - mjesto, ulica i broj</Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        name="korisnikAdresa"
                                        value={formData.korisnikAdresa}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touchedFields.korisnikAdresa && !validationErrors.korisnikAdresa}
                                        isInvalid={touchedFields.korisnikAdresa && !!validationErrors.korisnikAdresa}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikAdresa || 'Molimo unesite adresu.'}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} md="4" controlId="formKorisnikPosta">
                                    <Form.Label>Broj pošte</Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        name="korisnikBrojPoste"
                                        value={formData.korisnikBrojPoste}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touchedFields.korisnikBrojPoste && !validationErrors.korisnikBrojPoste}
                                        isInvalid={touchedFields.korisnikBrojPoste && !!validationErrors.korisnikBrojPoste}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikBrojPoste || 'Molimo unesite broj pošte.'}</Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} md="12" controlId="formKorisnikTelefon">
                                    <Form.Label>Telefon</Form.Label>
                                    <Form.Control
                                        required
                                        type="tel"
                                        name="korisnikTelefon"
                                        value={formData.korisnikTelefon}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touchedFields.korisnikTelefon && !validationErrors.korisnikTelefon}
                                        isInvalid={touchedFields.korisnikTelefon && !!validationErrors.korisnikTelefon}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikTelefon || 'Molimo unesite broj telefona.'}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} md="12" controlId="formKorisnikEmail">
                                    <Form.Label>Adresa e-pošte</Form.Label>
                                    <Form.Control
                                        required
                                        type="email"
                                        name="korisnikEmail"
                                        value={formData.korisnikEmail}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touchedFields.korisnikEmail && !validationErrors.korisnikEmail}
                                        isInvalid={touchedFields.korisnikEmail && !!validationErrors.korisnikEmail}
                                    />
                                    <Form.Control.Feedback
                                        type="invalid">{validationErrors.korisnikEmail || 'Molimo unesite ispravnu e-mail adresu.'}</Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                        </>
                    )}
                    {formData.nacinPristupa === 'appCert' && (
                        <>
                            <Form.Group className="mb-3" controlId="formNazivAppCert">
                                <Form.Label>Naziv aplikacijskog certifikata</Form.Label>
                                <Form.Control
                                    required
                                    type="text"
                                    name="nazivAplikacijskogCertifikata"
                                    value={formData.nazivAplikacijskogCertifikata}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touchedFields.nazivAplikacijskogCertifikata && !validationErrors.nazivAplikacijskogCertifikata}
                                    isInvalid={touchedFields.nazivAplikacijskogCertifikata && !!validationErrors.nazivAplikacijskogCertifikata}
                                />
                                <Form.Control.Feedback
                                    type="invalid">{validationErrors.nazivAplikacijskogCertifikata || 'Molimo unesite naziv aplikacijskog certifikata.'}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formNazivPosluzitelja">
                                <Form.Label>Naziv poslužitelja (FQDN)*</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nazivPosluzitelja"
                                    value={formData.nazivPosluzitelja}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touchedFields.nazivPosluzitelja && !validationErrors.nazivPosluzitelja}
                                    isInvalid={touchedFields.nazivPosluzitelja && !!validationErrors.nazivPosluzitelja}
                                />
                                <Form.Control.Feedback
                                    type="invalid">{validationErrors.nazivPosluzitelja || 'Molimo unesite naziv poslužitelja.'}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formEmailPoslovni">
                                <Form.Label>Adresa e-pošte poslovnog subjekta**</Form.Label>
                                <Form.Control
                                    required
                                    type="email"
                                    name="emailPoslovnogSubjekta"
                                    value={formData.emailPoslovnogSubjekta}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touchedFields.emailPoslovnogSubjekta && !validationErrors.emailPoslovnogSubjekta}
                                    isInvalid={touchedFields.emailPoslovnogSubjekta && !!validationErrors.emailPoslovnogSubjekta}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.emailPoslovnogSubjekta}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Text muted>*Poslužiteljski certifikat koristi se isključivo kod web servisa koji
                                funkcioniraju putem asinkrone razmjene poruka. U slučaju sinkrone razmjene, ovaj
                                certifikat nije potreban.</Form.Text>
                            <Form.Text muted>**Podatak je obavezan.</Form.Text>
                        </>
                    )}
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h5">5. Cjenovni Paket</Card.Header>
                <Card.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Paket Fina e-Račun PLUS obuhvaća usluge Fina e-Račun, Fina e-Arhiv i Fina Faktoring
                            platformu te korisnicima donosi niz pogodnosti u skladu s Uvjetima korištenja.</Form.Label>
                        {['XS 15', 'S 30', 'S 55', 'S 80', 'S 120', 'M 200', 'M 300', 'M 400', 'L 500', 'L 650', 'XL (650+)'].map(pkg => (
                            <Form.Check
                                type="radio"
                                key={pkg}
                                label={pkg}
                                name="cjenovniPaket"
                                value={pkg}
                                checked={formData.cjenovniPaket === pkg}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                id={`pkg-${pkg}`}
                                isInvalid={touchedFields.cjenovniPaket && !!validationErrors.cjenovniPaket}
                            />
                        ))}
                        <Form.Control.Feedback type="invalid"
                                               style={{display: touchedFields.cjenovniPaket && !!validationErrors.cjenovniPaket ? 'block' : 'none'}}>
                            {validationErrors.cjenovniPaket || 'Molimo odaberite cjenovni paket.'}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h5">6. Prava Korisnika</Card.Header>
                <Card.Body>
                    <Card.Text>
                        Korisniku se može dodijeliti <span className="font-weight-bold">jedno ili više prava</span> za
                        korištenje servisa.
                        <br/>
                        Za korištenje servisa Fina e-Arhiv obavezno je odabrati jedno od prava: „Administrator“ ili
                        „Korisnik“. Pravo „Korisnik“ moguće je dodijeliti samo ako je prethodno barem jednom korisniku
                        već dodijeljeno pravo „Administrator“.
                    </Card.Text>
                    <div id="pravaKorisnikaSection" className={validationErrors.pravaKorisnika ? 'is-invalid' : ''}>
                        <Row>
                            <Col md={4}>
                                <h6>Fina e-Račun</h6>
                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eRacunUnos" checked={formData.eRacunUnos}
                                                      onChange={handleChange}/>
                                    <Form.Check.Label>Unos</Form.Check.Label>
                                </Form.Check>
                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eRacunPotpisSlanje"
                                                      checked={formData.eRacunPotpisSlanje} onChange={handleChange}/>
                                    <Form.Check.Label>Potpis i slanje</Form.Check.Label>
                                </Form.Check>
                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eRacunPregledDohvat"
                                                      checked={formData.eRacunPregledDohvat} onChange={handleChange}/>
                                    <Form.Check.Label>Pregled i dohvat</Form.Check.Label>
                                </Form.Check>
                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eRacunSlanjeObavijestiPlacanje"
                                                      checked={formData.eRacunSlanjeObavijestiPlacanje}
                                                      onChange={handleChange}/>
                                    <Form.Check.Label>Slanje obavijesti o plaćanju subjekta</Form.Check.Label>
                                </Form.Check>

                                {sifraPJVrijednost ?
                                    <Form.Check>
                                        <OverlayTrigger
                                            overlay={<Tooltip id="eRacunPristupSvimPJ-disabled">Ako želite dodijeliti
                                                ista prava za sve poslovne jedinice, pod točkom 2. ne smije biti upisana
                                                šifra poslovne jedinice.</Tooltip>}>
                                            <span className="d-inline-block">
                                                <Form.Check.Input type="checkbox" name="eRacunPristupSvimPJ"
                                                                  disabled={true}/>
                                                <Form.Check.Label>Pristup svim poslovnim jedinicama<sup>1</sup></Form.Check.Label>
                                            </span>
                                        </OverlayTrigger>
                                    </Form.Check>
                                    :
                                    <Form.Check>
                                        <Form.Check.Input type="checkbox" name="eRacunPristupSvimPJ"
                                                          checked={formData.eRacunPristupSvimPJ}
                                                          onChange={handleChange}/>
                                        <Form.Check.Label>Pristup svim poslovnim
                                            jedinicama<sup>1</sup></Form.Check.Label>
                                    </Form.Check>
                                }

                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eRacunPravaDrugoIme"
                                                      checked={formData.eRacunPravaDrugoIme} onChange={handleChange}/>
                                    <Form.Check.Label>Prava u ime drugog poslovnog
                                        subjekta<sup>2</sup></Form.Check.Label>
                                </Form.Check>
                            </Col>
                            <Col md={4}>
                                <h6>Fina e-Arhiv</h6>
                                <Form.Check>
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={formData.eArhivAdministrator}
                                        onChange={() =>
                                            setFormData(prev => {
                                                const isSelected = prev.eArhivAdministrator;
                                                return {
                                                    ...prev,
                                                    eArhivAdministrator: !isSelected,
                                                    eArhivKorisnik: isSelected ? prev.eArhivKorisnik : false,
                                                };
                                            })
                                        }
                                        id="eArhivAdmin"
                                    />
                                    <Form.Check.Label>Administrator<sup>3</sup></Form.Check.Label>
                                </Form.Check>

                                <Form.Check>
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={formData.eArhivKorisnik}
                                        onChange={() =>
                                            setFormData(prev => {
                                                const isSelected = prev.eArhivKorisnik;
                                                return {
                                                    ...prev,
                                                    eArhivKorisnik: !isSelected,
                                                    eArhivAdministrator: isSelected ? prev.eArhivAdministrator : false,
                                                };
                                            })
                                        }
                                        id="eArhivKorisnik"
                                    />
                                    <Form.Check.Label>Korisnik<sup>4</sup></Form.Check.Label>
                                </Form.Check>
                                {sifraPJVrijednost ?
                                    <Form.Check>
                                        <OverlayTrigger
                                            overlay={<Tooltip id="eArhivPristupSvimPJ-disabled">Ako želite dodijeliti
                                                ista prava za sve poslovne jedinice, pod točkom 2. ne smije biti upisana
                                                šifra poslovne jedinice.</Tooltip>}>
                                            <span className="d-inline-block">
                                                <Form.Check.Input type="checkbox" name="eArhivPristupSvimPJ"
                                                                  disabled={true}/>
                                                <Form.Check.Label>Pristup svim poslovnim jedinicama<sup>1</sup></Form.Check.Label>
                                            </span>
                                        </OverlayTrigger>
                                    </Form.Check>
                                    :
                                    <Form.Check>
                                        <Form.Check.Input type="checkbox" name="eArhivPristupSvimPJ"
                                                          checked={formData.eArhivPristupSvimPJ}
                                                          onChange={handleChange}/>
                                        <Form.Check.Label>Pristup svim poslovnim
                                            jedinicama<sup>1</sup></Form.Check.Label>
                                    </Form.Check>
                                }

                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="eArhivPravaDrugoIme"
                                                      checked={formData.eArhivPravaDrugoIme} onChange={handleChange}/>
                                    <Form.Check.Label>Prava u ime drugog poslovnog
                                        subjekta<sup>2</sup></Form.Check.Label>
                                </Form.Check>
                            </Col>
                            <Col md={4}>
                                <h6>Fina Faktoring Platforma</h6>
                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="faktoringProdavateljPregled"
                                                      checked={formData.faktoringProdavateljPregled}
                                                      onChange={handleChange}/>
                                    <Form.Check.Label>Prodavatelj pregled<sup>5</sup></Form.Check.Label>
                                </Form.Check>

                                {disableFaktoringPotpisAndDuznik ?
                                    <>
                                        <Form.Check>
                                            <OverlayTrigger
                                                overlay={<Tooltip id="faktoringProdavateljPotpis-disabled">Ako Fina
                                                    Faktoring platformi pristupate aplikacijskim certifikatom, ne možete
                                                    odabrati pravo „Prodavatelj potpis“, jer je za potpisivanje
                                                    dokumenata potreban certifikat na Fina kriptouređaju (USB
                                                    stick/e-kartica).</Tooltip>}>
                                                <span className="d-inline-block">
                                                    <Form.Check.Input type="checkbox" name="faktoringProdavateljPotpis"
                                                                      disabled={true}/>
                                                    <Form.Check.Label>Prodavatelj potpis<sup>6</sup></Form.Check.Label>
                                                </span>
                                            </OverlayTrigger>
                                        </Form.Check>
                                        <Form.Check>
                                            <OverlayTrigger
                                                overlay={<Tooltip id="faktoringDuznik-disabled">Ako Fina Faktoring
                                                    platformi pristupate aplikacijskim certifikatom, ne možete odabrati
                                                    pravo „Dužnik“, jer je za potpisivanje dokumenata potreban
                                                    certifikat na Fina kriptouređaju (USB stick/e-kartica).</Tooltip>}>
                                                <span className="d-inline-block">
                                                    <Form.Check.Input type="checkbox" name="faktoringDuznik"
                                                                      disabled={true}/>
                                                    <Form.Check.Label>Dužnik<sup>7</sup></Form.Check.Label>
                                                </span>
                                            </OverlayTrigger>
                                        </Form.Check>
                                    </>
                                    :
                                    <>
                                        <Form.Check>
                                            <Form.Check.Input type="checkbox" name="faktoringProdavateljPotpis"
                                                              checked={formData.faktoringProdavateljPotpis}
                                                              onChange={handleChange}/>
                                            <Form.Check.Label>Prodavatelj potpis<sup>6</sup></Form.Check.Label>
                                        </Form.Check>
                                        <Form.Check>
                                            <Form.Check.Input type="checkbox" name="faktoringDuznik"
                                                              checked={formData.faktoringDuznik}
                                                              onChange={handleChange}/>
                                            <Form.Check.Label>Dužnik<sup>7</sup></Form.Check.Label>
                                        </Form.Check>
                                    </>
                                }

                                <Form.Check>
                                    <Form.Check.Input type="checkbox" name="faktoringPravaDrugoIme"
                                                      checked={formData.faktoringPravaDrugoIme}
                                                      onChange={handleChange}/>
                                    <Form.Check.Label>Prava u ime drugog poslovnog
                                        subjekta<sup>2</sup></Form.Check.Label>
                                </Form.Check>
                            </Col>
                        </Row>
                    </div>
                    <Form.Control.Feedback type="invalid"
                                           style={{display: validationErrors.pravaKorisnika ? 'block' : 'none'}}
                                           className="invalid-feedback-red">
                        {validationErrors.pravaKorisnika}
                    </Form.Control.Feedback>
                    <Card.Text>
                        <br/>
                        <span className="font-weight-bold">Napomena</span>: Ako Fina e-Računu pristupate aplikacijskim
                        certifikatom, obavezno je odabrati pravo „Pregled i dohvat “ i/ili „Potpis i slanje“, ovisno o
                        tome trebate li primati, slati ili obavljati obje funkcije u vezi s e-računima. Ako Fina
                        Faktoring platformi pristupate aplikacijskim certifikatom, ne možete odabrati prava „Prodavatelj
                        potpis“ i „Dužnik“, jer je za potpisivanje dokumenata potreban certifikat na Fina kriptouređaju
                        (USB stick/e-kartica).
                        <br/>
                        <br/>
                        <span className="font-weight-bold"><sup>1</sup>Pristup svim poslovnim jedinicama</span> - ako
                        korisniku želite dodijeliti ista prava za sve poslovne jedinice, ovdje označite pravo Pristup
                        svim poslovnim jedinicama.
                        <br/>
                        <span className="font-weight-bold"><sup>2</sup>Prava u ime drugog poslovnog subjekta</span> -
                        ako servis koristite u ime drugog subjekta, na pristupnici označite opciju „Prava u ime drugog
                        poslovnog subjekta“. Uz ovu Pristupnicu priložite Punomoć u kojoj vaš opunomoćitelj treba
                        označiti prava koja vam dodjeljuje. Obrazac punomoći dostupan je na web stranici <a
                        href="https://www.fina.hr/ngsite/content/download/16138/253366/1" target="_blank"
                        rel="noopener noreferrer">www.fina.hr</a>.
                        <br/>
                        <span className="font-weight-bold"><sup>3</sup>Administrator</span> - ovlaštena osoba s pravom
                        pristupa svim funkcionalnostima servisa Fina e-Arhiv. Može upravljati elektroničkim dokumentima
                        i sadržajem, te dodjeljivati prava drugim korisnicima za određene spremnike.
                        <br/>
                        <span className="font-weight-bold"><sup>4</sup>Korisnik</span> - osoba kojoj je Administrator
                        dodijelio prava za pristup i korištenje određenog spremnika u servisu Fina e-Arhiv.
                        <br/>
                        <span className="font-weight-bold"><sup>5</sup>Prodavatelj pregled</span> - omogućuje
                        prodavatelju tražbina uvid u dokumentaciju koja je predmet faktoringa te pristup svim
                        pripadajućim opcijama i informacijama.
                        <br/>
                        <span className="font-weight-bold"><sup>6</sup>Prodavatelj potpis</span> - uključuje sva prava
                        iz stavke "Prodavatelj pregled", uz dodatnu mogućnost potpisivanja dokumentacije.
                        <br/>
                        <span className="font-weight-bold"><sup>7</sup>Dužnik</span> - omogućuje korisniku uvid u
                        dokumentaciju vezanu uz faktoring i pravo na njeno potpisivanje.
                        <br/>
                        <br/>
                        Sukladno čl. 80. Zakona o porezu na dodanu vrijednost i čl. 161. Pravilnika o porezu na dodanu
                        vrijednost, izjavljujem da sam odabirom prava Pregled i prihvat i/ili Slanje obavijesti o
                        plaćanju te potpisom ove Pristupnice, suglasan/a zaprimati račune svih svojih dobavljača
                        dostavljenih putem servisa Fina e-Račun.
                    </Card.Text>
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Body>
                    <Row className="mb-3">
                        <Form.Group as={Col} md="4" controlId="formMjesto">
                            <Form.Label>Mjesto</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="mjesto"
                                value={formData.mjesto}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.mjesto && !validationErrors.mjesto}
                                isInvalid={touchedFields.mjesto && !!validationErrors.mjesto}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.mjesto || 'Molimo unesite mjesto.'}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="4" controlId="formDatum">
                            <Form.Label>Datum</Form.Label>
                            <Form.Control
                                required
                                type="date"
                                name="datum"
                                value={formData.datum}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.datum && !validationErrors.datum}
                                isInvalid={touchedFields.datum && !!validationErrors.datum}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.datum || 'Molimo odaberite datum.'}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="4" controlId="formOvlastenikImePrezime">
                            <Form.Label>Ime i prezime osobe ovlaštene za zastupanje</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                name="ovlastenikImePrezime"
                                value={formData.ovlastenikImePrezime}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isValid={touchedFields.ovlastenikImePrezime && !validationErrors.ovlastenikImePrezime}
                                isInvalid={touchedFields.ovlastenikImePrezime && !!validationErrors.ovlastenikImePrezime}
                            />
                            <Form.Control.Feedback
                                type="invalid">{validationErrors.ovlastenikImePrezime || 'Molimo unesite ime i prezime osobe ovlaštene za zastupanje.'}</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                    <Card.Text>
                        <span className="font-weight-bold">Informacije ispitaniku* o obradi osobnih podataka prikupljenih ovom Pristupnicom za ugovaranje paketa Fina e-Račun PLUS</span>
                        <br/>
                        <br/>
                        Voditelj obrade osobnih podataka: Financijska agencija, Ulica grada Vukovara 70, 10 000 Zagreb,
                        OIB: 85821130368, info@fina.hr / info telefon 0800 0080
                        <br/>
                        <br/>
                        Kontaktni podaci Službenika za zaštitu podataka: dpo@fina.hr
                        <br/>
                        <br/>
                        Pravna osnova i svrha obrade osobnih podataka: Osobni podatci prikupljeni ovom Pristupnicom za
                        ugovaranje paketa Fina e-Račun PLUS obrađuju se u svrhu pristupa servisima Fina e-Račun, Fina
                        e-Arhiv i Fina Faktoring platforma i nužni su za izvršenje ugovora u kojem je ispitanik stranka
                        (čl.6.1.b Opće uredbe o zaštiti podataka).
                        <br/>
                        <br/>
                        Primatelji osobnih podataka: Osobni podatci koji se prikupljaju ovom Pristupnicom ne predaju se
                        drugim primateljima. Pohrana osobnih podataka: Osobni podatci čuvat će se na način određen
                        propisima o arhiviranju, na rok od 10 godina od otkaza usluge ili od isteka certifikata na kojeg
                        se prava korištenja servisa odnose.
                        <br/>
                        <br/>
                        Prava ispitanika: Ispitanik ima pravo od Fine zatražiti pristup, ispravak, brisanje, prijenos
                        podataka i ograničavanje obrade te ima pravo podnijeti prigovor na obradu osobnih podataka koji
                        se na njega odnose, ako su za to ispunjene propisane pretpostavke. Zahtjev u vezi s prethodnim
                        pravima može se podnijeti u bilo kojoj poslovnoj jedinici Fine, pisanim putem na adresu
                        voditelja obrade ili na adresu elektroničke pošte Službenika za zaštitu podataka.
                        <br/>
                        <br/>
                        Pritužba na obradu osobnih podataka: Ispitanik ima pravo podnijeti pritužbu na obradu osobnih
                        podataka na adresu nadzornog tijela - Agencije za zaštitu osobnih podataka, na adresu
                        elektroničke pošte azop@azop.hr ili putem online obrasca na stranici <a
                        href="https://www.azop.hr" target="_blank" rel="noopener noreferrer">www.azop.hr</a>.
                        <br/>
                        <br/>
                        Prigovor na obradu osobnih podataka možete podnijeti nadzornom tijelu - Agenciji za zaštitu
                        osobnih podataka, Selska cesta 136, Zagreb, azop@azop.hr, <a href="https://www.azop.hr"
                                                                                     target="_blank"
                                                                                     rel="noopener noreferrer">www.azop.hr</a>
                        <br/>
                        <br/>
                        <br/>
                        *Ispitanik u smislu ove Pristupnice je ovlaštena osoba i korisnik servisa Fina e-Račun, Fina
                        e-Arhiv i Fina Faktoring platofrma, čiji se podatci prikupljaju ovom Pristupnicom.
                    </Card.Text>
                </Card.Body>
            </Card>
            <div className="d-flex justify-content-end mt-3">
                <Button
                    type="button"
                    variant="primary"
                    onClick={async () => {
                        const isValid = validateFormData();

                        if (!isValid) return;

                        const blob = await pdf(
                            <FilledPristupnicaDocument formData={formData}/>
                        ).toBlob();

                        saveAs(
                            blob,
                            `Pristupnica_${formData.naziv || 'firma'}_${new Date().toLocaleDateString("de-de").slice(0, 10)}.pdf`
                        );
                    }}
                >
                    Validiraj i preuzmi PDF
                </Button>
            </div>
        </Form>
    );
};

export default PristupnicaForm;